import { IMask } from "react-imask";
import { ChangeEvent, FormEvent, useState } from "react";
import { useRouter } from "next/router";

type MaskNumberOption = Omit<IMask.MaskedNumberOptions, "mask">;

const useMask = (option: IMask.AnyMaskedOptions) => {
  const [maskOption] = useState(option);

  const maskInstance = IMask.createMask(maskOption);

  const maskPipe = IMask.createPipe(
    maskInstance,
    IMask.PIPE_TYPE.UNMASKED,
    IMask.PIPE_TYPE.MASKED
  );
  const unMaskPipe = IMask.createPipe(
    maskInstance,
    IMask.PIPE_TYPE.MASKED,
    IMask.PIPE_TYPE.UNMASKED
  );

  const onChange = (
    e: ChangeEvent<HTMLInputElement> | FormEvent<HTMLInputElement>
  ) => {
    const unmasked = unMaskPipe((e.target as HTMLInputElement).value);
    const newValue = maskPipe(unmasked);
    (e.target as HTMLInputElement).value = newValue;
  };

  return {
    maskOption,
    maskInstance,
    onChange,
    maskPipe,
    unMaskPipe,
  };
};

const removeLeadingZeros = (value: string): string => {
  return value.replace(/^[0\D]+(\d)/, "$1");
};

export function useNullableNumberMask(option?: MaskNumberOption) {
  const { locale } = useRouter() || {};
  const maskOptions = {
    mask: Number,
    thousandsSeparator: ",",
    ...option,
  };
  const enNumberMask = useMask(maskOptions);
  const frNumberMask = useMask({
    ...maskOptions,
    thousandsSeparator: " ",
  });

  const mask = locale === "fr" ? frNumberMask : enNumberMask;
  return {
    ...mask,
    maskPipe: (input: string) => (input !== "" ? mask.maskPipe(input) : ""),
    unMaskPipe: (input: string) =>
      input !== "" ? +mask.unMaskPipe(input) : null,
  };
}

export function useNumberMask(option?: MaskNumberOption) {
  const { locale } = useRouter() || {};
  const maskOptions = {
    mask: Number,
    thousandsSeparator: ",",
    scale: 0,
    padFractionalZeros: false,
    ...option,
  };
  const enNumberMask = useMask(maskOptions);
  const frNumberMask = useMask({
    ...maskOptions,
    thousandsSeparator: " ",
  });

  const mask = locale === "fr" ? frNumberMask : enNumberMask;
  return {
    ...mask,
    unMaskPipe: (input: string) => +mask.unMaskPipe(removeLeadingZeros(input)),
  };
}

export function useDecimalMask(options?: MaskNumberOption) {
  const { locale } = useRouter() || {};
  const maskOptions = {
    mask: Number,
    scale: 2,
    thousandsSeparator: ",",
    padFractionalZeros: true,
    radix: ".",
    mapToRadix: ["."],
    ...options,
  };

  const enDecimalMask = useMask(maskOptions);
  const frDecimalMask = useMask({
    ...maskOptions,
    thousandsSeparator: " ",
    radix: ",",
  });

  const mask = locale === "fr" ? frDecimalMask : enDecimalMask;
  return {
    ...mask,
    unMaskPipe: (input: string) => +mask.unMaskPipe(input),
  };
}

export default useMask;
